import * as Stepper from '@studio/design-system/stepper';
import type { ReactElement } from 'react';
import { useStore } from './store/hooks.js';
import { Panel } from './store/index.js';
import type { StepsProps } from './types.js';

/**
 * `<Steps>` component.
 *
 * Three visible steps: Discover sources → Configure video → Connect controls
 * (Source = 1, Video = 2, and both Manufacturer + Control map to 3 via the
 * fallback). ENG-4159 relocated brand/model onto the "Configure video" RTSP
 * screen for RTSP-Assist flows (RTSP / ONVIF / manual RTSP); a non-RTSP source
 * without a modelId (e.g. NDI) still routes through the Manufacturer (driver +
 * nickname) panel between Video and Control.
 */
export const Steps = ({ panel, ...props }: StepsProps): ReactElement => {
  // ENG-3796: onboarding replaces the Connect controls panel with the funnel's
  // own connection test, so the third step is labelled for the screen the user
  // will actually get. Undefined for every other caller, which keeps the normal
  // Add Video Source stepper byte-identical.
  const onboarding = useStore((µ) => µ.onboarding);

  return (
    <Stepper.Root step={panel === Panel.Source ? 1 : panel === Panel.Video ? 2 : 3} {...props}>
      <Stepper.Step icon='source' label='Discover sources' />
      <Stepper.Step icon='camera' label='Configure video' />
      <Stepper.Step
        icon='joystick'
        label={onboarding === undefined ? 'Connect controls' : 'Test connection'}
      />
    </Stepper.Root>
  );
};
